import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import {
    Box,
    Card,
    CardContent,
    Typography,
    Stack,
    TextField,
    Chip,
    Button,
    IconButton,
    Tooltip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    CircularProgress,
    Snackbar,
    Alert,
    MenuItem,
    Autocomplete,
} from '@mui/material'
import LaunchIcon from '@mui/icons-material/Launch'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread'
import DraftsIcon from '@mui/icons-material/Drafts'
import EditIcon from '@mui/icons-material/Edit'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import PersonIcon from '@mui/icons-material/Person'
import { DataGrid } from '@mui/x-data-grid'

import { mainApi } from '../../store/services/mainApi'

// -------------------- external endpoints --------------------
const GMAIL_ORDERS_URL = 'https://tpomobi.shop/getAllGmailOrders'
const ORDER_ITEMS_URL = 'https://tpomobi.shop/parsePrezo'

// -------------------- helpers --------------------
const safeStr = (v) => (v === null || v === undefined ? '' : String(v))
const safeTrim = (v) => safeStr(v).trim()

// ✅ format: 12.02.2026 PLT 🌛
const formatDate = (date, isNight) => {
    const d = new Date(date)
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${dd}.${mm}.${yyyy} ${isNight ? 'PLT 🌛' : 'PLM ☀️'}`
}

const getDeliveryDate = (timeFlag) => {
    const now = new Date()
    return now.getUTCHours() < 7 ? formatDate(now, timeFlag) : formatDate(new Date(now.setDate(now.getDate() + 1)), timeFlag)
}

const DEFAULT_TPO_CONTACT_ID = '663a0484a20a70b531a72bb3'

const CONTACT_ID_BY_TRADE = {
    PDS: '66ee089ba5d74948be23e78a',
    MONK: '6993471bdfb30781faaf7f4e',
    STRP: '698cd1194898682fa8a18358',
    STR: '66983fadb9f522b8d76a1cd9',
    CNF: '6768b8ecd80a3e838f77d4bc',
    BLS: '666a1f50df7457b99f6711ea',
    FOCH: '6853431522f2f37e81766392',
    MRV: '66d4efa088790baf6e7b4b44',
}

const toneChipSx = {
    borderRadius: 2,
    fontWeight: 700,
    letterSpacing: 0.2,
}

const pageSx = {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #f6f7fb 0%, #ffffff 60%)',
    px: { xs: 2, md: 3 },
    py: { xs: 2, md: 3 },
}

const cardSx = {
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
}

const normalizeLoose = (s = '') =>
    safeStr(s)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\p{L}\p{N}]+/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim()

const getCurrentUserFromLocalStorage = () => {
    try {
        const raw = localStorage.getItem('acc')
        if (!raw) return null
        const u = JSON.parse(raw)
        if (!u || typeof u !== 'object') return null
        return {
            _id: safeTrim(u._id),
            email: safeTrim(u.email),
            name: safeTrim(u.name),
            phoneNumber: safeTrim(u.phoneNumber),
            role: safeTrim(u.role),
            userType: safeTrim(u.userType),
            username: safeTrim(u.username),
            site: safeTrim(u.site),
        }
    } catch {
        return null
    }
}

const actorLabel = (u) => safeTrim(u?.name) || safeTrim(u?.email) || safeTrim(u?.username) || safeTrim(u?._id) || 'Unknown'

// ✅ priority: name first, then nameProveedor
const productDisplayName = (p) => safeTrim(p?.name) || safeTrim(p?.nameProveedor) || safeTrim(p?.title) || ''



// --- normalize parsePrezo response to items[] ---
const normalizeParsePrezoResponseToItems = (payload) => {
    const rawMatched = Array.isArray(payload?.matchedItems) ? payload.matchedItems : null

    // if backend marks matched/unmatched, respect it; otherwise infer
    const inferIsMatched = (x) => {
        if (x?.isMatched !== undefined) return !!x.isMatched
        if (x?.matchReason && String(x.matchReason).toLowerCase().includes('unmatched')) return false
        // if it came from matchedItems list -> usually matched, but could still be broken; infer by presence of sku
        return !!safeTrim(x?.sku)
    }

    if (rawMatched) {
        return rawMatched.map((p, idx) => {
            const sku = safeTrim(p?.sku ?? `no-sku-${idx}`)
            const name = productDisplayName(p) || safeTrim(p?.title ?? '')
            const format = safeTrim(p?.__prezo?.format ?? p?.format ?? '')
            const qty = Number(p?.quantityOrder ?? p?.qty ?? p?.__prezo?.qty ?? 0) || 0

            return {
                _id: safeTrim(p?._id ?? `matched-${sku}-${idx}`),
                ...p,
                sku,
                name,
                format,
                qty,
                quantity: qty,
                quantityOrder: qty,
                __prezo: p?.__prezo ?? null,
                __raw: p?.__raw ?? null,
                matchReason: p?.matchReason || 'parsePrezo',
                isMatched: inferIsMatched(p),
            }
        })
    }

    const rawItems = Array.isArray(payload) ? payload : Array.isArray(payload?.items) ? payload.items : []
    return rawItems.map((it, idx) => {
        const sku = safeTrim(it?.sku ?? it?.__raw?.sku ?? it?.ref ?? it?.Ref ?? it?.id ?? it?._id ?? '')
        const name = productDisplayName(it) || safeTrim(it?.description ?? it?.Descripción ?? it?.descripcion ?? '')
        const format = safeTrim(it?.format ?? it?.Formato ?? it?.unit ?? it?.currentUnit ?? it?.__prezo?.format ?? '')
        const qty = Number(it?.qty ?? it?.quantity ?? it?.quantityOrder ?? it?.Cantidad ?? 0) || 0

        return {
            _id: safeTrim(it?._id ?? `prezo-${sku || name || idx}-${idx}`),
            sku,
            name,
            format,
            qty,
            quantity: qty,
            quantityOrder: qty,
            __raw: it,
            __prezo: it?.__prezo ?? null,
            matchReason: it?.matchReason || 'parsePrezo',
            isMatched: it?.isMatched !== undefined ? !!it.isMatched : !!sku,
        }
    })
}

const normalizeTradeKey = (v) =>
    safeTrim(v)
        .toUpperCase()
        .split(/\s+/)[0]
        .replace(/[^A-Z0-9]/g, '')

// -------------------- component --------------------
const PrezoPage = ({ deliveryType, time }) => {
    const dispatch = useDispatch()

    // current user
    const currentUser = useMemo(() => getCurrentUserFromLocalStorage(), [])
    const currentActor = useMemo(
        () => ({
            ...currentUser,
            label: actorLabel(currentUser),
            at: new Date().toISOString(),
            source: 'ui',
        }),
        [currentUser],
    )


    const [createOrder, { isLoading: isCreating }] = mainApi.useCreateNewOrderMutation()
    const [savePrezoOrderItems, { isLoading: isSavingByApi }] = mainApi.useSavePrezoOrderItemsMutation()
    const [createPrezoOrder, { isLoading: isCreatingPrezo }] = mainApi.useCreatePrezoOrderMutation?.() || []

    const [orders, setOrders] = useState([])
    const [loadingOrders, setLoadingOrders] = useState(false)
    const [ordersErr, setOrdersErr] = useState('')

    const [query, setQuery] = useState('')
    const [platformFilter, setPlatformFilter] = useState('all')

    // modal
    const [activeOrder, setActiveOrder] = useState(null)
    const [modalOpen, setModalOpen] = useState(false)


    const tradeKey = useMemo(() => {
        return normalizeTradeKey(
            activeOrder?.tradeName ||
            activeOrder?.userAcc?.tradeName ||
            localStorage.getItem('name') ||
            'TPO'
        )
    }, [activeOrder])

    const contactId = useMemo(() => {
        return CONTACT_ID_BY_TRADE[tradeKey] || DEFAULT_TPO_CONTACT_ID
    }, [tradeKey])

    const { data: userAcc, isLoading: isUserAccLoading } =
        mainApi.useGetContactsByIdQuery(contactId, {
            skip: !modalOpen || !contactId,
        })

    const [itemsLoading, setItemsLoading] = useState(false)
    const [itemsErr, setItemsErr] = useState('')
    const [items, setItems] = useState([])
    const [parsedMeta, setParsedMeta] = useState(null)

    const [dirty, setDirty] = useState(false)

    // last actor shown in modal (ui)
    const [lastActor, setLastActor] = useState(currentActor)

    const [toast, setToast] = useState({ open: false, severity: 'success', msg: '' })
    const showToast = (severity, msg) => setToast({ open: true, severity, msg })
    const closeToast = () => setToast((t) => ({ ...t, open: false }))

    // -------------------- double click protection --------------------
    // ✅ protects Create in modal from double click / duplicate orders
    const createLockRef = useRef(false)
    const [createLocked, setCreateLocked] = useState(false)

    const lockCreate = () => {
        if (createLockRef.current) return false
        createLockRef.current = true
        setCreateLocked(true)
        return true
    }
    const unlockCreate = () => {
        createLockRef.current = false
        setCreateLocked(false)
    }

    // -------------------- REPLACE PRODUCT UI --------------------
    const [replaceOpen, setReplaceOpen] = useState(false)
    const [replaceRow, setReplaceRow] = useState(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [replaceSelected, setReplaceSelected] = useState(null)

    const { data: products, isLoading: productsLoading } = mainApi.useGetProductsLimitQuery(
        { limit: 50, search: searchTerm },
        { skip: !replaceOpen },
    )

    const openReplaceDialog = (row) => {
        setReplaceRow(row)
        setReplaceSelected(null)
        setSearchTerm('')
        setReplaceOpen(true)
    }

    const closeReplaceDialog = () => {
        setReplaceOpen(false)
        setReplaceRow(null)
        setReplaceSelected(null)
        setSearchTerm('')
    }

    const applyReplace = () => {
        if (!replaceRow || !replaceSelected) return

        const fromSku = safeTrim(replaceRow.sku)
        const fromName = safeTrim(replaceRow.name ?? '')
        const toSku = safeTrim(replaceSelected.sku)
        const toName = productDisplayName(replaceSelected)
        const at = new Date().toISOString()

        setDirty(true)
        setLastActor(currentActor)

        setItems((prev) => {
            const key = replaceRow?._id || replaceRow?.sku
            const idx = prev.findIndex((x) => (x?._id || x?.sku) === key)
            if (idx === -1) return prev

            const next = [...prev]
            const current = next[idx] || {}

            const patched = {
                ...replaceSelected,
                _id: safeTrim(current?._id ?? replaceSelected?._id ?? `row-${idx}`),

                sku: safeTrim(replaceSelected.sku),
                name: productDisplayName(replaceSelected),

                qty: Number(current?.qty ?? current?.quantityOrder ?? 0) || 0,
                quantity: Number(current?.qty ?? current?.quantityOrder ?? 0) || 0,
                quantityOrder: Number(current?.qty ?? current?.quantityOrder ?? 0) || 0,
                format: safeTrim(current?.format ?? current?.__prezo?.format ?? ''),

                __prezo: current?.__prezo ?? null,
                __raw: current?.__raw ?? null,

                matchReason: 'manual-ui-replace',
                editedAt: at,

                __replacedFrom: { fromSku, fromName, at },

                // ✅ after manual replace we assume matched (sku exists in catalog)
                isMatched: true,
            }

            next[idx] = patched
            return next
        })

        showToast('success', `Replaced: ${fromSku || fromName} → ${toSku}`)
        closeReplaceDialog()
    }

    // -------------------- load orders --------------------
    const loadOrders = useCallback(async () => {
        setLoadingOrders(true)
        setOrdersErr('')
        try {
            const res = await fetch(GMAIL_ORDERS_URL)
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const data = await res.json()
            setOrders(Array.isArray(data) ? data : [])
        } catch (e) {
            setOrdersErr('Failed to load orders list. Check endpoint and CORS.')
            setOrders([])
        } finally {
            setLoadingOrders(false)
        }
    }, [])

    useEffect(() => {
        loadOrders()
    }, [loadOrders])

    const platformOptions = useMemo(() => {
        const set = new Set(orders.map((o) => safeStr(o?.platform).toLowerCase()).filter(Boolean))
        return ['all', ...Array.from(set).sort()]
    }, [orders])

    const stats = useMemo(() => {
        const total = orders.length
        const unread = orders.filter((o) => o?.gmail?.isUnread).length
        const byPlatform = orders.reduce((acc, o) => {
            const p = safeStr(o?.platform).toLowerCase() || 'unknown'
            acc[p] = (acc[p] || 0) + 1
            return acc
        }, {})
        return { total, unread, byPlatform }
    }, [orders])

    const filteredOrders = useMemo(() => {
        const q = query.trim().toLowerCase()
        return orders.filter((o) => {
            const p = safeStr(o?.platform).toLowerCase()
            const platformOk = platformFilter === 'all' ? true : p === platformFilter
            if (!platformOk) return false
            if (!q) return true
            const hay = [o?.tradeName, o?.deliveryAddress, o?.orderNumber, o?.link, o?.gmail?.subject].filter(Boolean).join(' ').toLowerCase()
            return hay.includes(q)
        })
    }, [orders, query, platformFilter])

    // -------------------- create CP order --------------------
    const createNewOrderInChangeOrder = async (itemsArg, selectedMatches) => {
        if (!activeOrder) {
            alert('Please select an order')
            return
        }

        const deliveryDate = safeTrim(activeOrder?.deliveryDate) ? safeTrim(activeOrder.deliveryDate).replaceAll('/', '.') : getDeliveryDate(time)

        if (isUserAccLoading || !userAcc?._id) {
            alert('userAcc еще грузится (contact). Подожди секунду и нажми Create еще раз.')
            return
        }

        const body = {
            data: {
                img: 'https://imgur.com/mpuLhYe.jpeg',
                items: itemsArg || [],
                date: Date.now(),
                type: 'browser',
                deliveryTime: time ? 'PLT 🌛' : 'PLM ☀️',
                deliveryDate,
                status: {},
                order: 'CP',
                paidValue: 0,
                orderDeliveryType: deliveryType,

                actor: { ...currentActor, action: 'create-cp-order' },

                userAcc: userAcc,
                tradeName: activeOrder?.tradeName ? activeOrder?.tradeName : '',
                selectedMatchesLast: selectedMatches,
                deliveryPrice: 0,
                link: activeOrder?.link,
                description: activeOrder?.link,
                realType: '',
                paymentType: 'Credit Line',
            },
        }

        await createOrder(body).unwrap?.()
        dispatch(mainApi.util.invalidateTags(['ShipmentOrders']))
        setLastActor(currentActor)
    }

    // -------------------- modal: load items --------------------
    const openOrderModal = async (order) => {
        setActiveOrder(order)
        setModalOpen(true)
        setItems([])
        setParsedMeta(null)
        setDirty(false)
        setItemsErr('')
        setItemsLoading(true)
        setLastActor(currentActor)

        // reset create lock when opening a new order
        unlockCreate()

        try {
            const res = await fetch(ORDER_ITEMS_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ link: order.link }),
            })
            if (!res.ok) throw new Error(`HTTP ${res.status}`)
            const data = await res.json()

            setParsedMeta(data)
            const normalizedItems = normalizeParsePrezoResponseToItems(data)
            setItems(normalizedItems)
        } catch (e) {
            setItemsErr('Failed to load items. Check ORDER_ITEMS_URL endpoint/params.')
            setItems([])
            setParsedMeta(null)
        } finally {
            setItemsLoading(false)
        }
    }

    const closeModal = () => {
        setModalOpen(false)
        setActiveOrder(null)
        setItems([])
        setParsedMeta(null)
        setItemsErr('')
        setDirty(false)
        unlockCreate()
    }

    // -------------------- correctness --------------------
    const correctness = useMemo(() => {
        const total = items.length
        const correct = items.filter((it) => !!it?.isMatched && !!safeTrim(it?.sku)).length
        return { total, correct }
    }, [items])

    // -------------------- save items (RTK Query) --------------------
    const onSaveItems = async ({ silentOkToast = false, allowCreatePrezoOrder = false } = {}) => {
        const orderNumber = safeTrim(activeOrder?.orderNumber)
        if (!orderNumber) return false
        if (!items.length) {
            showToast('warning', 'No items to save.')
            return false
        }

        const payloadItems = items.map((it) => ({
            sku: safeTrim(it?.sku || it?.__raw?.sku || ''),
            quantityOrder: Number(it?.qty ?? it?.quantityOrder ?? 0) || 0,
            quantity: Number(it?.qty ?? it?.quantityOrder ?? 0) || 0,

            qty: Number(it?.qty ?? it?.quantityOrder ?? 0) || 0,
            __prezo: it?.__prezo ? { ...it.__prezo } : null,
            __raw: it?.__raw ?? null,
            matchReason: it?.matchReason || (it?.__replacedFrom ? 'manual-ui-replace' : 'manual-save-ui'),
        }))

        try {
            const resp = await savePrezoOrderItems({
                orderNumber,
                items: payloadItems,
                actor: { ...currentActor, action: 'save-prezo-items' },
            }).unwrap?.()

            setDirty(false)
            setLastActor(currentActor)

            if (!silentOkToast) {
                showToast('success', `Saved ✅ autoApplied: ${resp?.autoAppliedCount ?? 0} • replacements: ${resp?.replacementsCount ?? 0}`)
            }
            return true
        } catch (err) {
            const status = err?.status || err?.originalStatus || err?.data?.status
            const msg = safeTrim(err?.data?.error || err?.error || err?.message || 'Save failed')

            if (status === 404 && allowCreatePrezoOrder && createPrezoOrder) {
                try {
                    await createPrezoOrder({
                        orderNumber,
                        platform: 'prezo',
                        actor: { ...currentActor, action: 'create-prezo-order' },
                    }).unwrap?.()

                    const resp2 = await savePrezoOrderItems({
                        orderNumber,
                        items: payloadItems,
                        actor: { ...currentActor, action: 'save-prezo-items' },
                    }).unwrap?.()

                    setDirty(false)
                    setLastActor(currentActor)

                    if (!silentOkToast) {
                        showToast('success', `Saved ✅ autoApplied: ${resp2?.autoAppliedCount ?? 0} • replacements: ${resp2?.replacementsCount ?? 0}`)
                    }
                    return true
                } catch (e2) {
                    const msg2 = safeTrim(e2?.data?.error || e2?.error || e2?.message || 'Create+Save failed')
                    showToast('error', msg2)
                    return false
                }
            }

            showToast('error', msg || 'Save failed')
            return false
        }
    }

    // ✅ Create from modal: save first, then create CP (LOCKED from double click)
    const onCreateFromModal = async () => {
        // lock immediately to prevent duplicates
        if (!lockCreate()) return

        try {
            if (!activeOrder) return

            const savedOk = await onSaveItems({ silentOkToast: true, allowCreatePrezoOrder: true })
            if (!savedOk) return

            await createNewOrderInChangeOrder(items, {
                source: 'gmail',
                platform: activeOrder?.platform,
                orderNumber: activeOrder?.orderNumber,
                tradeName: activeOrder?.tradeName,
                parsedOrderId: parsedMeta?.order?.id || null,
                actor: { ...currentActor, action: 'create-cp-order' },
            })

            showToast('success', 'Order created ✅')
        } catch (e) {
            console.error(e)
            showToast('error', safeTrim(e?.data?.error || e?.message || 'Create failed'))
        } finally {
            // keep locked a bit? better unlock so user can retry if failed
            unlockCreate()
        }
    }

    // -------------------- Orders columns --------------------
    const ordersColumns = useMemo(
        () => [
            {
                field: 'platform',
                headerName: 'Platform',
                width: 170,
                sortable: true,
                renderCell: (params) => {
                    const unread = !!params.row?.gmail?.isUnread
                    return (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={safeStr(params.value || '—').toUpperCase()} sx={{ ...toneChipSx }} variant="outlined" />
                            <Chip
                                size="small"
                                icon={unread ? <MarkEmailUnreadIcon /> : <DraftsIcon />}
                                label={unread ? 'UNREAD' : 'READ'}
                                color={unread ? 'warning' : 'success'}
                                sx={{ ...toneChipSx }}
                                variant="filled"
                            />
                        </Stack>
                    )
                },
            },
            {
                field: 'tradeName',
                headerName: 'Trade Name',
                width: 260,
                sortable: true,
                renderCell: (params) => (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.primary' }} noWrap>
                            {safeStr(params.value || '—')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                            #{safeStr(params.row?.orderNumber || '—')} · {safeStr(params.row?.brand || '—')}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: 'deliveryAddress',
                headerName: 'Address',
                flex: 1,
                minWidth: 320,
                sortable: false,
                renderCell: (params) => (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: 'text.primary' }} noWrap>
                            {safeStr(params.value || '—')}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                            {safeStr(params.row?.gmail?.subject || '')}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: 'date',
                headerName: 'Email Date',
                width: 200,
                sortable: true,
                valueGetter: (_, row) => row?.gmail?.date || '',
                renderCell: (params) => (
                    <Box>
                        <Typography variant="body2">{params.row?.gmail?.date || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {String(params.value || '')}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: 'lastActor',
                headerName: 'Last editor',
                width: 200,
                sortable: false,
                valueGetter: (_, row) => row?.lastActor?.name || row?.lastActor?.email || '',
                renderCell: (params) => {
                    const name = params.row?.lastActor?.name
                    const email = params.row?.lastActor?.email
                    const at = params.row?.lastActor?.at
                    return (
                        <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || email || '—'}</div>
                            <div style={{ fontSize: 12, opacity: 0.7, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {at ? new Date(at).toLocaleString() : ''}
                            </div>
                        </div>
                    )
                },
            },
            {
                field: 'actions',
                headerName: 'Actions',
                width: 260,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => {
                    const link = params.row?.link
                    return (
                        <Stack direction="row" spacing={1}>
                            <Tooltip title="Open original link">
                <span>
                  <IconButton
                      size="small"
                      onClick={(e) => {
                          e.stopPropagation()
                          if (link) window.open(link, '_blank')
                      }}
                      disabled={!link}
                  >
                    <LaunchIcon fontSize="small" />
                  </IconButton>
                </span>
                            </Tooltip>

                            <Tooltip title="Create (opens modal)">
                <span>
                  <Button
                      size="small"
                      variant="contained"
                      onClick={(e) => {
                          e.stopPropagation()
                          openOrderModal(params.row)
                      }}
                      disabled={isCreating}
                      startIcon={isCreating ? <CircularProgress size={14} color="inherit" /> : <AddCircleOutlineIcon />}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800, boxShadow: 'none' }}
                  >
                    Create
                  </Button>
                </span>
                            </Tooltip>
                        </Stack>
                    )
                },
            },
        ],
        [isCreating],
    )

    const ordersRows = useMemo(
        () =>
            filteredOrders.map((o, idx) => ({
                id: o?.gmail?.id || `${o?.orderNumber || 'ord'}-${idx}`,
                ...o,
            })),
        [filteredOrders],
    )

    // -------------------- Items grid (orange highlight for unmatched sku) --------------------
    const itemColumns = useMemo(
        () => [
            {
                field: '__replace',
                headerName: 'Replace',
                width: 130,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => (
                    <Tooltip title="Replace with another adminProducts item">
            <span>
              <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon fontSize="small" />}
                  onClick={(e) => {
                      e.stopPropagation()
                      openReplaceDialog(params.row)
                  }}
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
              >
                Replace
              </Button>
            </span>
                    </Tooltip>
                ),
            },
            {
                field: 'sku',
                headerName: 'SKU',
                width: 180,
                renderCell: (params) => (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                            {safeStr(params.value || '—')}
                        </Typography>
                        {params.row?.__replacedFrom?.fromSku ? (
                            <Typography variant="caption" color="text.secondary" noWrap>
                                replaced from: {safeStr(params.row.__replacedFrom.fromSku)}
                            </Typography>
                        ) : null}
                    </Box>
                ),
            },
            {
                field: 'name',
                headerName: 'Name',
                flex: 1,
                minWidth: 360,
                renderCell: (params) => (
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                            {safeStr(params.value || '—')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                            prezo: {safeStr(params.row?.__prezo?.name || params.row?.__raw?.name || params.row?.__raw?.description || '')}
                        </Typography>
                    </Box>
                ),
            },
            { field: 'format', headerName: 'Format', width: 220 },
            {
                field: 'qty',
                headerName: 'Qty',
                width: 110,
                type: 'number',
                editable: true,
                valueGetter: (params) => params.row?.qty ?? params.row?.quantityOrder ?? 0,
                renderCell: (params) => params.row?.qty,
            },
        ],
        [],
    )

    const itemsRows = useMemo(
        () =>
            items.map((it, idx) => ({
                id: it?._id || it?.sku || `item-${idx}`,
                ...it,
            })),
        [items],
    )

    const processRowUpdate = (newRow, oldRow) => {
        setDirty(true)
        setLastActor(currentActor)
        setItems((prev) => {
            const keyOld = oldRow?._id || oldRow?.sku
            const idx = prev.findIndex((x) => (x?._id || x?.sku) === keyOld)
            if (idx === -1) return prev
            const next = [...prev]
            const patched = { ...next[idx], ...newRow }

            if (patched.qty !== undefined) patched.qty = Number(patched.qty) || 0
            patched.quantityOrder = Number(patched.qty ?? patched.quantityOrder ?? 0) || 0

            next[idx] = patched
            return next
        })
        return newRow
    }

    // ✅ highlight row if sku not matched in adminProducts
    const getItemRowClassName = useCallback((params) => {
        const isMatched = params?.row?.isMatched
        const sku = safeTrim(params?.row?.sku)
        if (!sku) return 'row-unmatched'
        if (isMatched === false) return 'row-unmatched'
        return ''
    }, [])

    const productsOptions = Array.isArray(products) ? products : []

    // ✅ label = NAME FIRST (not nameProveedor)
    const productLabel = (p) => productDisplayName(p)

    // search across everything, incl nameProveedor
    const filterProducts = (options, state) => {
        const q = normalizeLoose(state.inputValue)
        if (!q) return options
        return options.filter((p) => {
            const hay = [
                p?.sku,
                p?.name, // priority display, but also searchable
                p?.nameProveedor,
                p?.description,
                p?.__prezo?.name,
                p?.__raw?.name,
                p?.__raw?.description,
            ]
                .map((x) => normalizeLoose(x))
                .join(' | ')
            return hay.includes(q)
        })
    }

    const savingAny = !!isSavingByApi || !!isCreatingPrezo
    const lastActorText = useMemo(() => `${actorLabel(lastActor)} • ${safeStr(lastActor?.at || '')}`, [lastActor])

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
                {/* Header */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-end' }} justifyContent="space-between" sx={{ mb: 2 }}>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 900, letterSpacing: -0.2 }}>
                            Gmail Orders — Prezo / Platforms
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Click a row to open the modal. Replace → backend will update replacements + dictionary after Save / Create.
                        </Typography>
                    </Box>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                        <TextField
                            size="small"
                            label="Search"
                            placeholder="tradeName, address, orderNumber, subject…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            sx={{ minWidth: { xs: '100%', md: 360 } }}
                        />
                        <TextField size="small" select label="Platform" value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)} sx={{ minWidth: { xs: '100%', md: 180 } }}>
                            {platformOptions.map((p) => (
                                <MenuItem key={p} value={p}>
                                    {p === 'all' ? 'All' : p}
                                </MenuItem>
                            ))}
                        </TextField>

                        <Tooltip title="Refresh">
              <span>
                <IconButton onClick={loadOrders} disabled={loadingOrders}>
                  <RefreshIcon />
                </IconButton>
              </span>
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* Stats */}
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    <Card sx={{ ...cardSx, flex: 1 }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="overline" color="text.secondary">
                                    Total orders
                                </Typography>
                                <Chip label={stats.total} color="info" size="small" sx={toneChipSx} />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Filtered: <b>{filteredOrders.length}</b>
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card sx={{ ...cardSx, flex: 1 }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="overline" color="text.secondary">
                                    Unread (Gmail)
                                </Typography>
                                <Chip label={stats.unread} color={stats.unread ? 'warning' : 'success'} size="small" sx={toneChipSx} />
                            </Stack>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Field: <code>gmail.isUnread</code>
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card sx={{ ...cardSx, flex: 1 }}>
                        <CardContent>
                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="overline" color="text.secondary">
                                    Platforms
                                </Typography>
                                <Chip label={Object.keys(stats.byPlatform).length} size="small" sx={toneChipSx} />
                            </Stack>

                            <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                                {Object.entries(stats.byPlatform)
                                    .sort((a, b) => b[1] - a[1])
                                    .slice(0, 8)
                                    .map(([k, v]) => (
                                        <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined" sx={{ ...toneChipSx, mb: 1 }} />
                                    ))}
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>

                {/* Orders grid */}
                <Card sx={cardSx}>
                    {loadingOrders ? <LinearProgress /> : null}

                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                Orders
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {ordersErr ? 'Load error' : `Shown: ${ordersRows.length}`}
                            </Typography>
                        </Stack>

                        {ordersErr ? (
                            <Alert severity="error" sx={{ mb: 1 }}>
                                {ordersErr}
                            </Alert>
                        ) : null}

                        <Box sx={{ height: 620, width: '100%' }}>
                            <DataGrid
                                rows={ordersRows}
                                columns={ordersColumns}
                                disableRowSelectionOnClick
                                pageSizeOptions={[25, 50, 100]}
                                initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
                                onRowClick={(params) => openOrderModal(params.row)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2.5,
                                    '& .MuiDataGrid-columnHeaders': { backgroundColor: 'rgba(2, 6, 23, 0.02)', fontWeight: 900 },
                                    '& .MuiDataGrid-row': { cursor: 'pointer' },
                                }}
                            />
                        </Box>
                    </Box>
                </Card>

                {/* Modal */}
                <Dialog open={modalOpen} onClose={closeModal} fullWidth maxWidth="xl">
                    <DialogTitle sx={{ pr: 7 }}>
                        <Stack spacing={0.5}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography sx={{ fontWeight: 900 }}>{activeOrder ? `Order #${safeStr(activeOrder.orderNumber)} · ${safeStr(activeOrder.tradeName)}` : 'Order'}</Typography>

                                <Chip
                                    icon={<CheckCircleIcon />}
                                    label={`Correct: ${correctness.correct}/${correctness.total}`}
                                    size="small"
                                    color={correctness.correct === correctness.total ? 'success' : 'warning'}
                                    sx={toneChipSx}
                                />

                                <Chip icon={<PersonIcon />} label={`Last actor: ${lastActorText}`} size="small" variant="outlined" sx={toneChipSx} />
                            </Stack>

                            <Typography variant="caption" color="text.secondary">
                                Save → /savePrezoOrderItems (backend logs replacements + dictionary). Create → Save first, then create CP.
                            </Typography>
                        </Stack>

                        <IconButton onClick={closeModal} sx={{ position: 'absolute', right: 12, top: 12 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers sx={{ backgroundColor: '#fbfcff' }}>
                        {activeOrder ? (
                            <Stack spacing={2} sx={{ mb: 2 }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                    <Card sx={{ ...cardSx, flex: 1 }}>
                                        <CardContent>
                                            <Typography variant="overline" color="text.secondary">
                                                Trade Name
                                            </Typography>
                                            <Typography sx={{ fontWeight: 900 }}>{safeStr(activeOrder.tradeName || '—')}</Typography>
                                            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                                <Chip label={safeStr(activeOrder.platform || '—')} size="small" variant="outlined" sx={toneChipSx} />
                                                <Chip label={`Brand: ${safeStr(activeOrder.brand || '—')}`} size="small" variant="outlined" sx={toneChipSx} />
                                            </Stack>
                                        </CardContent>
                                    </Card>

                                    <Card sx={{ ...cardSx, flex: 2 }}>
                                        <CardContent>
                                            <Typography variant="overline" color="text.secondary">
                                                Address
                                            </Typography>
                                            <Typography sx={{ fontWeight: 700 }}>{safeStr(activeOrder.deliveryAddress || '—')}</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                {safeStr(activeOrder.gmail?.subject || '')}
                                            </Typography>
                                        </CardContent>
                                    </Card>

                                    <Card sx={{ ...cardSx, flex: 1 }}>
                                        <CardContent>
                                            <Typography variant="overline" color="text.secondary">
                                                Parsed (Prezo)
                                            </Typography>
                                            <Typography sx={{ fontWeight: 800 }}>Order ID: {safeStr(parsedMeta?.order?.id || '—')}</Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                Date: {safeStr(parsedMeta?.order?.date || '—')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                Customer: {safeStr(parsedMeta?.order?.customer || '—')}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                Supplier: {safeStr(parsedMeta?.order?.supplier || '—')}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Stack>

                                <Divider />
                            </Stack>
                        ) : null}

                        {itemsLoading ? (
                            <Card sx={{ ...cardSx }}>
                                <LinearProgress />
                                <CardContent>
                                    <Stack spacing={1.2}>
                                        <Typography sx={{ fontWeight: 900 }}>Loading items…</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Please wait
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ) : itemsErr ? (
                            <Alert severity="error">{itemsErr}</Alert>
                        ) : (
                            <Card sx={{ ...cardSx }}>
                                <CardContent>
                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                                                Items
                                            </Typography>
                                            <Chip label={items.length} size="small" sx={toneChipSx} variant="outlined" />
                                            {dirty ? <Chip label="Unsaved changes" color="warning" size="small" sx={toneChipSx} /> : <Chip label="Saved" color="success" size="small" sx={toneChipSx} />}
                                            {items.some((x) => x?.isMatched === false) ? (
                                                <Chip label="Some items not found in catalog" color="warning" size="small" sx={toneChipSx} variant="outlined" />
                                            ) : null}
                                        </Stack>

                                        <Typography variant="caption" color="text.secondary">
                                            Qty is editable; Replace uses adminProducts. Unmatched rows are highlighted.
                                        </Typography>
                                    </Stack>

                                    <Box sx={{ height: 520, width: '100%' }}>
                                        <DataGrid
                                            rows={itemsRows}
                                            columns={itemColumns}
                                            processRowUpdate={processRowUpdate}
                                            onProcessRowUpdateError={(err) => {
                                                console.error(err)
                                                showToast('error', 'Row update failed.')
                                            }}
                                            disableRowSelectionOnClick
                                            pageSizeOptions={[25, 50, 100]}
                                            initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
                                            getRowClassName={getItemRowClassName}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: 'divider',
                                                borderRadius: 2.5,
                                                '& .MuiDataGrid-columnHeaders': { backgroundColor: 'rgba(2, 6, 23, 0.02)', fontWeight: 900 },
                                                // ✅ transparent orange row highlight
                                                '& .row-unmatched': {
                                                    backgroundColor: 'rgba(255, 152, 0, 0.16)',
                                                },
                                                '& .row-unmatched:hover': {
                                                    backgroundColor: 'rgba(255, 152, 0, 0.22)',
                                                },
                                            }}
                                        />
                                    </Box>

                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                                        Save sends sku + qty + __prezo + matchReason + actor. Backend stores replacements + dictionary automatically.
                                    </Typography>
                                </CardContent>
                            </Card>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ px: 2.5, py: 1.5 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: '100%' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip label={activeOrder?.link ? 'Link available' : 'No link'} size="small" variant="outlined" sx={toneChipSx} />
                            </Stack>

                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button
                                    variant="outlined"
                                    onClick={() => activeOrder?.link && window.open(activeOrder.link, '_blank')}
                                    disabled={!activeOrder?.link}
                                    startIcon={<LaunchIcon />}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                                >
                                    Open original
                                </Button>

                                <Button
                                    variant="outlined"
                                    onClick={() => onSaveItems({ silentOkToast: false, allowCreatePrezoOrder: true })}
                                    disabled={savingAny || itemsLoading || !dirty}
                                    startIcon={savingAny ? <CircularProgress size={16} /> : <SaveIcon />}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                                >
                                    Save
                                </Button>

                                <Button
                                    variant="contained"
                                    onClick={onCreateFromModal}
                                    disabled={isCreating || itemsLoading || savingAny || createLocked}
                                    startIcon={isCreating || savingAny || createLocked ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutlineIcon />}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900, boxShadow: 'none' }}
                                >
                                    Create order
                                </Button>
                            </Stack>
                        </Stack>
                    </DialogActions>
                </Dialog>

                {/* Replace dialog */}
                <Dialog open={replaceOpen} onClose={closeReplaceDialog} fullWidth maxWidth="md">
                    <DialogTitle sx={{ pr: 7 }}>
                        <Stack spacing={0.5}>
                            <Typography sx={{ fontWeight: 900 }}>Replace product</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Display: <b>name</b> first (then nameProveedor). Search works across SKU / name / nameProveedor / description / prezo fields.
                            </Typography>
                        </Stack>

                        <IconButton onClick={closeReplaceDialog} sx={{ position: 'absolute', right: 12, top: 12 }}>
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers sx={{ backgroundColor: '#fbfcff' }}>
                        <Stack spacing={2}>
                            <Card sx={{ ...cardSx }}>
                                <CardContent>
                                    <Typography variant="overline" color="text.secondary">
                                        Current row
                                    </Typography>
                                    <Typography sx={{ fontWeight: 900 }}>
                                        {safeStr(replaceRow?.name ?? '—')}
                                        {replaceRow?.sku ? ` (${safeStr(replaceRow.sku)})` : ''}
                                    </Typography>

                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        prezo: {safeStr(replaceRow?.__prezo?.name || replaceRow?.__raw?.name || replaceRow?.__raw?.description || '')}
                                    </Typography>
                                </CardContent>
                            </Card>

                            <Autocomplete
                                value={replaceSelected}
                                onChange={(_, v) => setReplaceSelected(v)}
                                options={productsOptions}
                                loading={productsLoading}
                                isOptionEqualToValue={(a, b) => safeTrim(a?._id) === safeTrim(b?._id)}
                                getOptionLabel={productLabel}
                                filterOptions={filterProducts}
                                renderOption={(props, p) => (
                                    <li {...props} key={safeTrim(p?._id)}>
                                        <Stack spacing={0} sx={{ width: '100%' }}>
                                            <Typography sx={{ fontWeight: 800 }}>{productDisplayName(p) || '—'}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                sku: {safeStr(p?.sku || '—')} • nameProveedor: {safeStr(p?.nameProveedor || '—')}
                                            </Typography>
                                        </Stack>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Search product"
                                        placeholder="Start typing sku / name / nameProveedor / prezo…"
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {productsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />

                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                Important: qty/format are kept from Prezo, but the product itself is taken fully from adminProducts. Manual replacements will be added to the backend auto-replacement dictionary.
                            </Alert>
                        </Stack>
                    </DialogContent>

                    <DialogActions sx={{ px: 2.5, py: 1.5 }}>
                        <Stack direction="row" spacing={1} sx={{ width: '100%' }} justifyContent="flex-end">
                            <Button variant="outlined" onClick={closeReplaceDialog} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}>
                                Cancel
                            </Button>
                            <Button variant="contained" onClick={applyReplace} disabled={!replaceSelected} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900, boxShadow: 'none' }}>
                                Apply
                            </Button>
                        </Stack>
                    </DialogActions>
                </Dialog>

                <Snackbar open={toast.open} autoHideDuration={3200} onClose={closeToast} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                    <Alert onClose={closeToast} severity={toast.severity} variant="filled" sx={{ borderRadius: 2 }}>
                        {toast.msg}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    )
}

export default PrezoPage
